// NEMOS · ORDER — specialist. Reads the `notes` + `note_folders` tables NEMOS Notes
// uses: unfiled = no folder (or folder gone), unnamed = still the default title,
// stale = unnamed and untouched for 14+ days.
import { supabase } from '../../../../lib/supabase';
import { dateKeyOf } from '../../time';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, openAction, useLoad } from './util';

const BASE = { id: 'nemos', toolId: 'nemos', orderFunction: 'revise', title: 'ORDER', deityName: 'NEMOS', agentId: 'nemos' } as const;
const STALE_MS = 14 * 86400000;

interface Order {
  unfiled: number;
  unnamed: number;
  stale: number;
  /** Distinct notes that are unfiled or unnamed. */
  need: number;
}

export function useNemosWidget(c: WidgetCtx): OrbWidgetModel {
  const uid = c.userId;
  const dayKey = dateKeyOf(c.now);
  const { value: o } = useLoad<Order>(
    uid
      ? async () => {
          const [n, f] = await Promise.all([
            supabase.from('notes').select('id, folder_id, title, updated_at').eq('user_id', uid).limit(1000),
            supabase.from('note_folders').select('id').eq('user_id', uid).limit(1000),
          ]);
          if (n.error || f.error) throw new Error((n.error || f.error)?.message);
          const folders = new Set(((f.data ?? []) as Array<{ id: string }>).map((x) => x.id));
          const notes = (n.data ?? []) as Array<{ folder_id: string | null; title: string | null; updated_at: string | null }>;
          const now = Date.now();
          let unfiled = 0;
          let unnamed = 0;
          let stale = 0;
          let need = 0;
          for (const r of notes) {
            const orphan = !r.folder_id || !folders.has(r.folder_id);
            const t = (r.title ?? '').trim();
            const blank = t === '' || t === 'New Note';
            if (orphan) unfiled++;
            if (orphan || blank) need++;
            if (blank) {
              unnamed++;
              const at = r.updated_at ? new Date(r.updated_at).getTime() : NaN;
              if (Number.isFinite(at) && now - at > STALE_MS) stale++;
            }
          }
          return { unfiled, unnamed, stale, need };
        }
      : null,
    [uid, dayKey, c.tick],
  );

  const total = o ? o.need : 0;
  if (!o || total === 0) return emptyModel(BASE, c, 'Everything is filed');

  const lines: string[] = [];
  if (o.unfiled) lines.push(`${o.unfiled} unfiled note${o.unfiled === 1 ? '' : 's'}`);
  if (o.unnamed) lines.push(`${o.unnamed} unnamed (unclassified)`);
  if (o.stale) lines.push(`${o.stale} stale 14d+`);

  return {
    ...BASE,
    status: 'attention',
    summary: `${total} item${total === 1 ? '' : 's'} need order`,
    metrics: [
      { label: 'Unfiled', value: o.unfiled, tone: o.unfiled ? 'warning' : 'neutral' },
      { label: 'Unnamed', value: o.unnamed, tone: o.unnamed ? 'warning' : 'neutral' },
      { label: 'Stale', value: o.stale, tone: o.stale ? 'warning' : 'neutral' },
    ],
    alert: `${total} item${total === 1 ? '' : 's'} need order`,
    lines,
    primaryAction: { label: 'Put in order', run: () => c.openTool('nemos'), tone: 'gold' },
    secondaryActions: [openAction(c, 'nemos', 'Nemos')],
    signals: { alerted: true },
  };
}
